import { getAuthToken } from './api';

// Browsers cannot set an Authorization header on a WebSocket handshake, so the
// PocketBase users token is passed as a query parameter. The token is re-read on
// every (re)connect, so a token refreshed after login is picked up automatically.
const buildUrl = () => {
  const token = getAuthToken();
  return `wss://api.future-of-audio.com/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  //return `ws://localhost:8081${token ? `?token=${encodeURIComponent(token)}` : ''}`;
};

// Auto-reconnecting WebSocket wrapper. The underlying WebSocket is replaced on
// each reconnect, but handlers registered via `onmessage`/addEventListener live
// on the wrapper and are re-emitted, so consumers that assign `socket.onmessage`
// once keep receiving updates after a dropped connection without a page reload.
// (Previously the raw socket was created once with no reconnect, so any drop -
// idle timeout, network blip, server restart - silently stopped all updates.)
class ReconnectingSocket {
  constructor(urlFactory, { minDelayMs = 1000, maxDelayMs = 10000 } = {}) {
    this._urlFactory = urlFactory;
    this._minDelayMs = minDelayMs;
    this._maxDelayMs = maxDelayMs;
    this._delayMs = minDelayMs;
    this._shouldRun = true;
    this._ws = null;
    this._on = {}; // onmessage / onopen / onclose / onerror
    this._listeners = { message: new Set(), open: new Set(), close: new Set(), error: new Set() };
    this._connect();
  }

  _connect() {
    let ws;
    try {
      ws = new WebSocket(this._urlFactory());
    } catch (e) {
      this._scheduleReconnect();
      return;
    }
    this._ws = ws;

    ws.onopen = (event) => {
      this._delayMs = this._minDelayMs; // reset backoff on a healthy connection
      this._emit('open', event);
    };
    ws.onmessage = (event) => this._emit('message', event);
    ws.onerror = (event) => this._emit('error', event);
    ws.onclose = (event) => {
      this._emit('close', event);
      this._scheduleReconnect();
    };
  }

  _scheduleReconnect() {
    if (this._shouldRun === false) return;
    const delay = this._delayMs;
    this._delayMs = Math.min(this._delayMs * 2, this._maxDelayMs); // exponential backoff
    setTimeout(() => { if (this._shouldRun) this._connect(); }, delay);
  }

  _emit(type, event) {
    const handler = this._on['on' + type];
    if (handler) { try { handler(event); } catch (e) { /* consumer handler threw */ } }
    this._listeners[type].forEach((fn) => { try { fn(event); } catch (e) { /* listener threw */ } });
  }

  addEventListener(type, fn) { if (this._listeners[type]) this._listeners[type].add(fn); }
  removeEventListener(type, fn) { if (this._listeners[type]) this._listeners[type].delete(fn); }

  send(data) {
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(data);
      return true;
    }
    return false;
  }

  close() {
    this._shouldRun = false; // stop reconnecting
    if (this._ws) { try { this._ws.close(); } catch (e) { /* ignore */ } }
  }

  get readyState() { return this._ws ? this._ws.readyState : WebSocket.CONNECTING; }

  get onmessage() { return this._on.onmessage; }
  set onmessage(fn) { this._on.onmessage = fn; }
  get onopen() { return this._on.onopen; }
  set onopen(fn) { this._on.onopen = fn; }
  get onclose() { return this._on.onclose; }
  set onclose(fn) { this._on.onclose = fn; }
  get onerror() { return this._on.onerror; }
  set onerror(fn) { this._on.onerror = fn; }
}

const socket = new ReconnectingSocket(buildUrl);

export default socket;
